<template>
  <div>
    <DModal :show-close="false" :isBgOverlay="true" :isCloseOnBgClick="false" @close="$emit('close')" :appendToBody="true"
            style="z-index: 12312312312312" :placeInTop="true">
      <div class="d-modal text-dark" slot="insertComponent">
        <div class="d-modal__header">
          <div>
            <h2>Indberettede engangsydelser i OPUS</h2>
            <p class="d-modal__subtitle">
              {{ userName }} · {{ employmentLabel }}
            </p>
          </div>
          <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
            <span class="icon-cross2"></span>
          </div>
        </div>

        <div class="d-modal__toolbar">
          <DoDatePickerRange
            :allowSameDate="true"
            :startDate="startDate"
            :endDate="endDate"
            :maximumDateRange="365"
            :dateShortCutsProps="dateShortCuts"
            @startChanged="startDateChanged"
            @endChanged="endDateChanged">
          </DoDatePickerRange>
          <button class="do-bttn do-bttn--submit" @click="openCreate">
            Opret engangsydelse
          </button>
        </div>

        <div class="d-modal__content">
          <Loader v-if="isLoading" :isLoaderModal="true"></Loader>

          <template v-else>
            <table class="do-table">
              <thead>
              <tr>
                <th class="td-date">DATO</th>
                <th class="td-loenkode">LØNKODE</th>
                <th class="td-units">ENHEDER</th>
                <th class="td-units">BELØB</th>
                <th>OMK.STED</th>
                <th class="td-system">SYSTEM</th>
                <th class="td-status">STATUS</th>
                <th class="th-actions"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="displayedPayments.length === 0">
                <td :colspan="8" class="italic">
                  Ingen engangsydelser i OPUS for perioden
                </td>
              </tr>
              <tr v-for="row in displayedPayments" :key="row.id" class="engangsydelse-row">
                <td class="td-date">{{ row.vDato }}</td>
                <td class="td-loenkode">{{ row.vLoenart }}</td>
                <td class="td-units">{{ formatDecimal(row.antal) }}</td>
                <td class="td-units">{{ formatDecimal(row.beloeb) }}</td>
                <td>{{ row.afv_omkostningssted || '—' }}</td>
                <td class="td-system">{{ row.afsendelses_system || '—' }}</td>
                <td class="td-status">
                  <span class="do-pill do-pill--small" :class="row.statusClass">{{ row.vStatus }}</span>
                </td>
                <td class="td-actions">
                  <button
                    v-if="row.vStatus === 'I OPUS'"
                    type="button"
                    class="do-bttn do-bttn--small modregn-btn"
                    @click.stop="openModregn(row)"
                  >Modregn</button>
                </td>
              </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </DModal>

    <CreateEngangsydelse
      v-if="isShowCreate"
      :schoolId="schoolId"
      :employmentIds="[employmentId]"
      :subtitle="effectiveCreateSubtitle"
      :prefill="createPrefill"
      @created="onCreated"
      @close="closeCreate">
    </CreateEngangsydelse>
  </div>
</template>

<script>
import DModal from '../../common/DModal.vue'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'
import Loader from '../../common/Loader.vue'
import CreateEngangsydelse from './CreateEngangsydelse.vue'
import moment from 'moment'
import eventbus from '../../../eventbus'
import docendo from '../../../docendo'

export default {
  name: 'EngangsydelserModal',
  components: {DModal, DoDatePickerRange, Loader, CreateEngangsydelse},
  props: {
    schoolId: {
      type: Number,
      required: true
    },
    employmentId: {
      required: true
    },
    employment: {
      default: null
    },
    userId: {
      required: true
    },
    userName: {
      type: String,
      default: ''
    },
    employmentNumber: {
      default: ''
    }
  },
  data () {
    return {
      // Standardperiode: en måned tilbage til og med dags dato – det er denne periode der hentes fra OPUS.
      startDate: moment().subtract(1, 'month'),
      endDate: moment(),
      isLoading: false,
      payments: [],
      isShowCreate: false,
      createPrefill: null,
      createModalSubtitle: '',
      // Netop oprettede ydelser (fra opret-modalen) vises øverst med kø-status, indtil OPUS har behandlet dem.
      pendingPayments: []
    }
  },
  computed: {
    employmentLabel () {
      const e = this.employment
      if (!e) return this.employmentNumber ? String(this.employmentNumber) : ''
      const title = e.source_title || e.title || 'Ansættelse'
      return e.employment_number ? e.employment_number + ' - ' + title : title
    },
    createSubtitle () {
      return this.userName + (this.employmentLabel ? ' – ' + this.employmentLabel : '')
    },
    effectiveCreateSubtitle () {
      return this.createModalSubtitle || this.createSubtitle
    },
    // Kun disse genveje i datovælgeren – ingen skoleår.
    dateShortCuts () {
      const now = moment()
      return [
        {title: 'Denne uge', startDate: now.clone().startOf('isoWeek'), endDate: now.clone().endOf('isoWeek')},
        {title: 'Denne måned', startDate: now.clone().startOf('month'), endDate: now.clone().endOf('month')},
        {title: 'Sidste måned', startDate: now.clone().subtract(1, 'month').startOf('month'), endDate: now.clone().subtract(1, 'month').endOf('month')}
      ]
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    // Lønkoder slås op på den fulde lønkode (4 cifre = lønart + løbenummer).
    salaryCodesByLoenkode () {
      const map = {}
      for (let i = 0; i < this.salaryCodes.length; i++) {
        const code = this.salaryCodes[i]
        if (code.loenart) map[String(code.loenart)] = code
      }
      return map
    },
    displayedPayments () {
      const opusRows = this.payments
        .slice()
        .sort((a, b) => new Date(a.startdato) - new Date(b.startdato))
        .map(item => {
          const loenkode = this.loenkodeForRow(item)
          return Object.assign({}, item, {
            vLoenart: this.loenartLabel(loenkode),
            vDato: item.startdato ? moment(item.startdato).format('DD-MM-YYYY') : '',
            vStatus: 'I OPUS',
            statusClass: 'do-pill--green'
          })
        })
      // Netop oprettede ydelser vises øverst med kø-status, til OPUS har behandlet dem.
      return this.pendingPayments.concat(opusRows)
    }
  },
  methods: {
    formatDecimal (value) {
      if (value == null || value === '') return ''
      return String(value).replace('.', ',')
    },
    // Rekonstruerer den fulde lønkode (lønart polstret til 3 cifre + løbenummer) for en række.
    loenkodeForRow (item) {
      if (item.loenart == null) return ''
      const loenart = String(item.loenart).padStart(3, '0')
      const loebenummer = item.loebenummer != null ? String(item.loebenummer) : '0'
      return loenart + loebenummer
    },
    // Bygger den viste lønkode-tekst (kode + evt. navn fra Docendos lønkoder).
    loenartLabel (loenkode) {
      const salaryCode = loenkode ? this.salaryCodesByLoenkode[loenkode] : null
      const codeName = (salaryCode && salaryCode.name) || ''
      return codeName
        ? (loenkode ? loenkode + ' - ' + codeName : codeName)
        : (loenkode || '—')
    },
    statusLabel (status) {
      switch (status) {
        case 'success': return 'Sendt'
        case 'error': return 'Fejlet'
        default: return 'I kø'
      }
    },
    statusPillClass (status) {
      switch (status) {
        case 'success': return 'do-pill--green'
        case 'error': return 'do-pill--red'
        default: return 'do-pill--yellow'
      }
    },
    // Normaliserer et beløb/antal til fast præcision, så fx 2 og 2,00 matcher.
    normAmount (v) {
      if (v == null || v === '') return ''
      const n = parseFloat(String(v).replace(',', '.'))
      return isNaN(n) ? '' : n.toFixed(2)
    },
    // Bygger en sammenlignelig nøgle (lønkode + dato + antal + beløb) til at matche en kø-række mod en OPUS-række.
    matchKey (loenkode, startdato, antal, beloeb) {
      const date = startdato ? moment(startdato).format('YYYY-MM-DD') : ''
      return [loenkode || '', date, this.normAmount(antal), this.normAmount(beloeb)].join('|')
    },
    // DoDatePickerRange udsender både startChanged og endChanged ved et periodeskift. Vi samler dem
    // til ét opslag, så vi ikke laver to live-OPUS-kald (det andet med en halvt opdateret periode).
    startDateChanged (date) {
      this.startDate = date
      this.scheduleRead()
    },
    endDateChanged (date) {
      this.endDate = date
      this.scheduleRead()
    },
    scheduleRead () {
      if (this._readTimer) clearTimeout(this._readTimer)
      this._readTimer = setTimeout(() => {
        this._readTimer = null
        this.read()
      }, 0)
    },
    read () {
      this.isLoading = true
      const fromDate = moment(this.startDate).format('YYYY-MM-DD')
      const toDate = moment(this.endDate).format('YYYY-MM-DD')
      docendo.salaryOpusRead(this.schoolId, [this.schoolId], fromDate, toDate, this.userId, success => {
        const list = (success && Array.isArray(success.one_time_payments)) ? success.one_time_payments : []
        // OPUS-opslaget returnerer alle medarbejderens ansættelsesnumre; vis kun denne ansættelse.
        const empNumber = parseInt(this.employmentNumber, 10)
        this.payments = isNaN(empNumber)
          ? list
          : list.filter(p => parseInt(p.medarbejdernummer, 10) === empNumber)
        // Fjern kø-rækker, der nu er dukket op i OPUS, så de ikke vises dobbelt.
        const opusKeys = {}
        this.payments.forEach(p => {
          opusKeys[this.matchKey(this.loenkodeForRow(p), p.startdato, p.antal, p.beloeb)] = true
        })
        this.pendingPayments = this.pendingPayments.filter(row => !opusKeys[row._key])
        this.isLoading = false
      }, failure => {
        console.log('Kunne ikke hente engangsydelser fra OPUS', failure)
        this.payments = []
        this.isLoading = false
        eventbus.toast = {text: 'Kunne ikke hente engangsydelser fra OPUS', success: false, position: 'bottomLeft'}
      })
    },
    openCreate () {
      this.createPrefill = null
      this.createModalSubtitle = ''
      this.isShowCreate = true
    },
    closeCreate () {
      this.isShowCreate = false
      this.createPrefill = null
      this.createModalSubtitle = ''
    },
    // OPUS-rækker kan ikke ændres direkte; korrektion sker via en modregning (negativt antal/beløb).
    openModregn (row) {
      const loenkode = this.loenkodeForRow(row)
      const salaryCode = loenkode ? this.salaryCodesByLoenkode[loenkode] : null
      const antal = row.antal != null && row.antal !== '' ? parseFloat(String(row.antal).replace(',', '.')) : null
      const beloeb = row.beloeb != null && row.beloeb !== '' ? parseFloat(String(row.beloeb).replace(',', '.')) : null
      this.createModalSubtitle = 'Modregn – ' + this.userName + ' – ' + this.employmentLabel
      this.createPrefill = {
        salaryCodeId: salaryCode ? salaryCode.id : null,
        antal: antal != null && !isNaN(antal) ? -antal : null,
        beloeb: beloeb != null && !isNaN(beloeb) ? -beloeb : null,
        dato: row.vDato ? moment(row.vDato, 'DD-MM-YYYY') : (row.startdato ? moment(row.startdato) : null)
      }
      this.isShowCreate = true
    },
    onCreated (created) {
      this.closeCreate()
      this.$emit('created')
      // Vis de netop oprettede ydelser øverst med kø-status. OPUS behandler dem asynkront, så de
      // optræder først i selve OPUS-listen efter behandling – derfor holder vi dem som "pending" her.
      const list = Array.isArray(created) ? created : (created ? [created] : [])
      const rows = []
      list.forEach(item => {
        const ydelser = Array.isArray(item.engangsydelser) ? item.engangsydelser : []
        ydelser.forEach((e, idx) => {
          const loenkode = e.Loenart
            ? String(parseInt(e.Loenart, 10)).padStart(3, '0') + (e.Loebenummer != null ? String(e.Loebenummer) : '0')
            : ''
          rows.push({
            id: 'pending-' + item.id + '-' + idx,
            // Nøgle til at genkende ydelsen når den senere dukker op i OPUS-listen, så kø-rækken kan fjernes.
            _key: this.matchKey(loenkode, e.Startdato, e.Antal, e.Beloeb),
            vDato: e.Startdato ? moment(e.Startdato).format('DD-MM-YYYY') : '',
            vLoenart: this.loenartLabel(loenkode),
            antal: e.Antal != null ? e.Antal : null,
            beloeb: e.Beloeb != null ? e.Beloeb : null,
            afv_omkostningssted: e.Afv_Omkostningssted || '',
            vStatus: this.statusLabel(item.status),
            statusClass: this.statusPillClass(item.status)
          })
        })
      })
      this.pendingPayments = rows.concat(this.pendingPayments)
    }
  },
  mounted () {
    this.$store.dispatch('readSalaryCodes')
    this.read()
  },
  beforeDestroy () {
    if (this._readTimer) clearTimeout(this._readTimer)
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.d-modal {
  width: 900px;
  max-width: calc(100vw - 32px);
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.d-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #6b7280;
}

.d-modal__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
}

.d-modal__content {
  padding: 1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.do-pill--small {
  display: inline-block;
  padding: 0 0.5rem;
  font-size: 10px;
  line-height: 18px;
  border-radius: 10px;
  text-transform: uppercase;
}

.do-pill--green {
  background: #dcfce7;
  border-color: #bbf7d0;
  color: #166534;
}

.do-pill--red {
  background: #fee2e2;
  border-color: #fecaca;
  color: #991b1b;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table tbody td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.do-table tbody tr:hover {
  background-color: aliceblue;
}

.td-date {
  width: 110px;
}

.td-status {
  width: 90px;
}

.td-system {
  width: 100px;
}

.td-units {
  text-align: right;
  width: 90px;
}

.do-table thead th.td-loenkode,
.do-table thead th.td-units {
  text-align: right;
}

.td-loenkode {
  text-align: right;
}

.th-actions {
  width: 100px;
}

.td-actions {
  width: 100px;
  text-align: right;
  position: relative;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
}

.modregn-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.12s ease;
}

.engangsydelse-row:hover .modregn-btn {
  opacity: 1;
  pointer-events: auto;
}
</style>



// WEBPACK FOOTER //
// EngangsydelserModal.vue?0468ffd0